import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { Container, Form, Card, ListGroup, Row, Col, Badge, Button, Alert } from 'react-bootstrap';
import api from '../api/axios';
import AddServiceRecord from '../components/AddServiceRecord';
import RegisterVehicle from '../components/RegisterVehicle';
import TodayReservations from '../components/TodayReservations';
import BayStatus from '../components/BayStatus';
import ReservationModal from '../components/modals/ReservationModal';
import SettingsContext from '../context/SettingsContext';
import dayjs from 'dayjs';
import './HomePage.css';
import { formatDateTime } from '../utils/format';

const HomePage = () => {
    const { settings } = useContext(SettingsContext);
    const [plateNumber, setPlateNumber] = useState('');
    const [vehicle, setVehicle] = useState(null);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState(''); // 오류가 아닌 안내 (차량 없는 고객을 고른 경우 등)
    const searchInputRef = useRef(null);
    const [suggestions, setSuggestions] = useState([]);
    
    // 예약 모달 상태 — checkInReservation 이 null 이면 신규 예약 등록 모드
    const [showCheckInModal, setShowCheckInModal] = useState(false);
    const [checkInReservation, setCheckInReservation] = useState(null);
    // 신규 등록 시 기본 날짜·시각. 렌더마다 새 값이면 모달 useEffect 가 폼을 리셋하므로 state 로 고정
    const [newReservationDate, setNewReservationDate] = useState(null);
    const [reservationRefreshKey, setReservationRefreshKey] = useState(0);

    const [showRegistration, setShowRegistration] = useState(false);
    const [carModels, setCarModels] = useState([]);

    const [vehicleSizes, setVehicleSizes] = useState([]);
    const [selectedSizeId, setSelectedSizeId] = useState('');

    useEffect(() => {
        api.get('/carmodels').then(res => setCarModels(res.data.data.filter(m => m.isActive))).catch(err => console.error("Error fetching car models:", err));
        api.get('/vehiclesizes').then(res => {
            setVehicleSizes(res.data.data.sizes);
        }).catch(err => console.error("Error fetching vehicle sizes:", err));
    }, []);

    const resetState = () => {
        setVehicle(null);
        setShowRegistration(false);
        setSelectedSizeId('');
        setError('');
        setNotice('');
    };

    const performSearch = async (numberToSearch, isNewVehicle = false) => {
        setVehicle(null);
        setShowRegistration(false);
        setSelectedSizeId('');
        setError('');
        setNotice('');

        try {
            const response = await api.get(`/vehicles/${numberToSearch}`);
            const fetchedVehicle = response.data.data;
            setVehicle(fetchedVehicle);
            
            if (fetchedVehicle.vehicle_size_id) {
                setSelectedSizeId(fetchedVehicle.vehicle_size_id);
            } else if (isNewVehicle && settings.default_vehicle_size_id) {
                setSelectedSizeId(settings.default_vehicle_size_id);
                api.put(`/vehicles/${fetchedVehicle.id}`, { vehicle_size_id: settings.default_vehicle_size_id })
                     .catch(err => console.error("Failed to auto-save default vehicle size:", err));
            }

        } catch (err) {
            if (err.response && err.response.status === 404) {
                setShowRegistration(true);
            } else {
                setError('차량 조회 중 오류가 발생했습니다.');
                console.error(err);
            }
        }
        setSuggestions([]);
    };

    // 🚀 지능형 검색 및 디바운싱 적용
    const debouncedSearch = useMemo(() => {
        let timeoutId;
        return (val) => {
            if (timeoutId) clearTimeout(timeoutId);
            
            const cleanVal = val.trim().replace(/[^0-9a-zA-Z가-힣]/g, '');
            
            if (cleanVal.length < 2) {
                setSuggestions([]);
                return;
            }

            timeoutId = setTimeout(async () => {
                try {
                    const response = await api.get(`/vehicles/search?term=${encodeURIComponent(cleanVal)}`);
                    setSuggestions(response.data.data);
                } catch (err) {
                    console.error('Error fetching suggestions:', err);
                }
            }, 300);
        };
    }, []);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setPlateNumber(value);
        debouncedSearch(value);
        
        setVehicle(null);
        setShowRegistration(false);
        setError('');
        setNotice('');
    };

    const handleSuggestionClick = (suggestion) => {
        // 통합검색 케이스3(차량 없는 고객)은 plateNumber 가 NULL 이다 (server/routes/vehicles.js).
        // 그대로 performSearch(null) 하면 GET /vehicles/null → 404 → 차량번호 없는
        // 신규 등록 폼으로 빠지면서 고객 정보가 버려진다.
        if (!suggestion.plateNumber) {
            setSuggestions([]);
            setPlateNumber('');
            setNotice(`${suggestion.customerName || '이 고객'}님은 등록된 차량이 없습니다. 차량번호를 입력해 조회해주세요.`);
            searchInputRef.current?.focus();
            return;
        }
        setPlateNumber(suggestion.plateNumber);
        performSearch(suggestion.plateNumber);
    };

    // Enter: 제안이 있으면 첫 번째를 고른다 (목록을 손으로 누르는 것과 동일 — 모달과 같은 규칙).
    // 제안이 없으면 막지 않고 Form onSubmit 의 직접 조회로 넘긴다.
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && suggestions.length > 0) {
            e.preventDefault();
            handleSuggestionClick(suggestions[0]);
        }
    };
    
    const handleSaveSuccess = () => {
        const currentPlate = plateNumber;
        resetState();
        setPlateNumber(currentPlate);
        performSearch(currentPlate);
        setReservationRefreshKey(prev => prev + 1);
    };

    const handleSizeChange = async (e) => {
        const newSizeId = e.target.value;
        setSelectedSizeId(newSizeId);
        if (vehicle && newSizeId) {
            try {
                await api.put(`/vehicles/${vehicle.id}`, { vehicle_size_id: newSizeId });
            } catch (err) {
                console.error("Failed to save vehicle size:", err);
            }
        }
    };

    const handleCheckIn = (reservation) => {
        setCheckInReservation(reservation);
        setShowCheckInModal(true);
    };

    // 달력 없이 예약 등록 — 모달의 신규 모드(reservation=null)를 여는 유일한 진입점
    const handleNewReservation = () => {
        setCheckInReservation(null);
        setNewReservationDate(dayjs().add(1, 'hour').startOf('hour').toDate());
        setShowCheckInModal(true);
    };

    return (
        <>
            <div className="hero-section">
                <Container>
                    <h1>현장 접수 · 차량 조회</h1>
                    <p>차량번호 · 고객명 · 연락처로 조회하고 바로 접수하세요</p>
                    <Form onSubmit={(e) => { e.preventDefault(); if(plateNumber) performSearch(plateNumber); }} className="search-form">
                        <Row className="g-2">
                            <Col>
                                <Form.Group controlId="plateNumberSearch" style={{ position: 'relative' }}>
                                    <Form.Label className="visually-hidden">통합 검색</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="차량번호, 고객명, 연락처 입력..."
                                        value={plateNumber}
                                        onChange={handleInputChange}
                                        onKeyDown={handleKeyDown}
                                        autoComplete="off"
                                        size="lg"
                                        ref={searchInputRef}
                                    />
                                    {suggestions.length > 0 && (
                                        <ListGroup className="suggestions-list">
                                            {suggestions.map(s => (
                                                <ListGroup.Item
                                                    key={s.id ?? `c${s.customer_id}`}
                                                    action
                                                    // action 은 <button> 으로 렌더되고 이 목록은 검색 form 안에 있다.
                                                    // type 을 주지 않으면 submit 이 되어, 제안을 고른 직후 form 이
                                                    // 한 번 더 조회를 돌려 방금 세운 상태(안내 문구 등)를 덮어쓴다.
                                                    type="button"
                                                    onClick={() => handleSuggestionClick(s)}
                                                    className="d-flex justify-content-between align-items-center"
                                                >
                                                    <span className="hp-sug-main">
                                                        {s.plateNumber
                                                            ? <strong className="text-primary">{s.plateNumber}</strong>
                                                            : <Badge bg="secondary" className="fw-normal">차량 미등록</Badge>}
                                                        <span className="text-muted">{s.customerName || '이름없음'}</span>
                                                        {s.phoneNumber && <small className="hp-sug-phone">{s.phoneNumber}</small>}
                                                    </span>
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col xs="auto">
                                <Button type="submit" variant="primary" size="lg" className="h-100 px-4">조회</Button>
                            </Col>
                        </Row>
                    </Form>
                </Container>
            </div>

            <ReservationModal
                show={showCheckInModal}
                onHide={() => setShowCheckInModal(false)}
                reservation={checkInReservation}
                selectedDate={newReservationDate}
                onSaveSuccess={() => {
                    setShowCheckInModal(false);
                    setReservationRefreshKey(prev => prev + 1);
                }}
            />

            <Container>
                {!!settings.use_reservation && (
                    <TodayReservations
                        onCheckIn={handleCheckIn}
                        onNewReservation={handleNewReservation}
                        refreshTrigger={reservationRefreshKey}
                    />
                )}

                {error && <p className="text-danger mt-3 text-center">{error}</p>}
                {notice && (
                    <Alert variant="info" className="mt-3 py-2 small text-center">
                        <i className="bi bi-info-circle me-1"></i>{notice}
                    </Alert>
                )}

                {showRegistration && (
                    <RegisterVehicle 
                        plateNumber={plateNumber}
                        carModels={carModels}
                        isCarModelRequired={settings.is_car_model_required}
                        onRegisterSuccess={(plate) => performSearch(plate, true)}
                    />
                )}

                {vehicle && (
                    <>
                        <Card className="mt-4 border-0 shadow-sm">
                            <Card.Header as="h5" className="bg-white d-flex justify-content-between align-items-center py-3">
                                <span>
                                    <i className="bi bi-car-front me-2"></i>
                                    {vehicle.plateNumber}
                                    <span className="ms-2 h6 text-muted">({vehicle.manufacturer || '차종 미지정'} {vehicle.modelName})</span>
                                </span>
                                {vehicle.customerName && (
                                    <Badge bg="success" pill className="px-3 py-2">
                                        {vehicle.customerName}님 / {vehicle.points.toLocaleString()} P
                                    </Badge>
                                )}
                            </Card.Header>
                            <Card.Body>
                                {vehicle.comment && <Card.Subtitle className="mb-3 text-muted fst-italic">"{vehicle.comment}"</Card.Subtitle>}
                                <div className="timeline-container">
                                    {vehicle.serviceHistory.length > 0 ? (
                                        vehicle.serviceHistory.map((record, index) => (
                                            <div className="timeline-item" key={record.id}>
                                                <div className="timeline-icon">{vehicle.serviceHistory.length - index}</div>
                                                <Card className="timeline-card border-0 shadow-none bg-light">
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between">
                                                            <div>
                                                                <Card.Title as="h6" className="d-inline-block me-2 fw-bold">{record.menuName}</Card.Title>
                                                                {record.paymentMethod && <Badge bg="secondary" className="small">{record.paymentMethod}</Badge>}
                                                            </div>
                                                            <strong className="text-primary">{parseInt(record.finalPrice || 0).toLocaleString()}원</strong>
                                                        </div>
                                                        <Card.Subtitle className="mb-2 text-muted small">{formatDateTime(record.startTime || record.created_at)}</Card.Subtitle>
                                                        {record.selectedOptions && <p className="mb-1 small text-dark"><strong>옵션:</strong> {record.selectedOptions}</p>}
                                                        {record.comment && <p className="mb-0 small fst-italic text-muted"><strong>메모:</strong> {record.comment}</p>}
                                                    </Card.Body>
                                                </Card>
                                            </div>
                                        ))
                                    ) : <p className="text-center py-4 text-muted">첫 방문 차량입니다. 환영합니다!</p>}
                                </div>
                            </Card.Body>
                            <Card.Footer className="bg-white border-top-0 py-3">
                                <Form.Group as={Row} controlId="vehicleSizeSelect" className="align-items-center">
                                    <Form.Label column sm="3" className="fw-bold">차량 사이즈</Form.Label>
                                    <Col sm="9">
                                        <Form.Select value={selectedSizeId} onChange={handleSizeChange} className="border-primary">
                                            <option value="">-- 가격 계산을 위해 사이즈를 선택하세요 --</option>
                                            {vehicleSizes.map(size => <option key={size.id} value={size.id}>{size.sizeName}</option>)}
                                        </Form.Select>
                                    </Col>
                                </Form.Group>
                            </Card.Footer>
                        </Card>
                    </>
                )}
                
                {vehicle && selectedSizeId && (
                    <div className="mt-4">
                        <AddServiceRecord
                            vehicle={vehicle}
                            sizeId={selectedSizeId}
                            onSave={handleSaveSuccess}
                        />
                    </div>
                )}

                {!!settings.use_reservation && (
                    <BayStatus refreshTrigger={reservationRefreshKey} />
                )}
            </Container>
        </>
    );
};

export default HomePage;
